import type { FundingFaxSendResponse } from "@credit-acceptance/step6-bff-client";
import { FundingFaxSendResponseStatusEnum } from "@credit-acceptance/step6-bff-client";
import { useMutation } from "@tanstack/react-query";

import { fundingFaxSendApi } from "../../../api/client";
import { isHttpError } from "../../../lib/query/errors";

/**
 * Translation keys for the "Send Funding Fax" failure toasts.
 */
type SendFundingFaxErrorKey =
  | "actions.sendFundingFaxForbidden"
  | "actions.sendFundingFaxNotFound"
  | "actions.sendFundingFaxNoFaxNumber"
  | "actions.sendFundingFaxUpstreamError"
  | "actions.sendFundingFaxError";

/**
 * Translation keys for the outcome of a 200 response.
 */
type FundingFaxResultKey =
  | "actions.sendFundingFaxSuccess"
  | "actions.sendFundingFaxSuppressed"
  | "actions.sendFundingFaxError";

/**
 * Maps a failed send to the i18n key for the message shown to the analyst. The
 * endpoint's documented failures each have a distinct remedy — a missing dealer
 * fax number is the analyst's to fix, an upstream recap-service error is worth
 * retrying — so they must not collapse into one generic message.
 * @param {unknown} error - The rejection from `sendFundingFax`.
 * @returns {SendFundingFaxErrorKey} The translation key for the error toast.
 */
export function sendFundingFaxErrorKey(error: unknown): SendFundingFaxErrorKey {
  if (isHttpError(error, 403)) {
    return "actions.sendFundingFaxForbidden";
  }
  if (isHttpError(error, 404)) {
    return "actions.sendFundingFaxNotFound";
  }
  if (isHttpError(error, 422)) {
    return "actions.sendFundingFaxNoFaxNumber";
  }
  if (isHttpError(error, 502) || isHttpError(error, 504)) {
    return "actions.sendFundingFaxUpstreamError";
  }
  return "actions.sendFundingFaxError";
}

/**
 * Classifies a 200 response. Only an explicit `SENT` proves the dealer actually
 * received a fax: `SUPPRESSED` means the fax switch stopped the send, and a
 * missing or unrecognised `status` (the field is optional in the generated
 * model) tells us nothing. Because the analyst acts on this confirmation, both
 * non-`SENT` cases are reported as failures rather than assumed successful.
 * @param {FundingFaxSendResponse} response - The send response body.
 * @returns {{ sent: boolean; key: FundingFaxResultKey }} Whether the fax went out, and the message to show.
 */
export function fundingFaxResult(response: FundingFaxSendResponse): { sent: boolean; key: FundingFaxResultKey } {
  if (response.status === FundingFaxSendResponseStatusEnum.Sent) {
    return { sent: true, key: "actions.sendFundingFaxSuccess" };
  }
  if (response.status === FundingFaxSendResponseStatusEnum.Suppressed) {
    return { sent: false, key: "actions.sendFundingFaxSuppressed" };
  }
  return { sent: false, key: "actions.sendFundingFaxError" };
}

/**
 * Mutation hook backing the "Send Funding Fax" action. Calls
 * `FundingFaxSendApi.sendFundingFax` (POST, no body), which forwards to
 * recap-service to generate the funding-fax PDF and send it through EasyLink.
 * The endpoint is NOT idempotent — each call sends another fax — so every call
 * site must block repeat invocations while `isPending` is true.
 * @param {string} approvalId - The numeric approval / application id.
 * @returns {ReturnType<typeof useMutation>} The TanStack Mutation result.
 */
export function useSendFundingFax(approvalId: string) {
  return useMutation({
    mutationFn: async (): Promise<FundingFaxSendResponse> => {
      const response = await fundingFaxSendApi.sendFundingFax(approvalId);
      return response.data;
    },
  });
}
